import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export const useConfirmDialog = (enabled = true) => {
  const [showDialogFlag, setShowDialogFlag] = useState(false);
  const enabledRef = useRef(enabled);
  const pendingRef = useRef<{
    promise: Promise<boolean>;
    resolve: (confirmed: boolean) => void;
  } | null>(null);

  const finish = useCallback((confirmed: boolean) => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setShowDialogFlag(false);
    pending?.resolve(confirmed);
  }, []);

  const cancel = useCallback(() => finish(false), [finish]);
  const accept = useCallback(() => finish(true), [finish]);

  useLayoutEffect(() => {
    enabledRef.current = enabled;
    if (!enabled) cancel();
  }, [enabled, cancel]);

  useLayoutEffect(
    () => () => {
      enabledRef.current = false;
      pendingRef.current?.resolve(false);
      pendingRef.current = null;
    },
    [],
  );

  const confirm = useCallback(() => {
    if (!enabledRef.current) return Promise.resolve(false);
    if (pendingRef.current) return pendingRef.current.promise;

    let resolve!: (confirmed: boolean) => void;
    const promise = new Promise<boolean>((settle) => {
      resolve = settle;
    });
    pendingRef.current = { promise, resolve };
    setShowDialogFlag(true);
    return promise;
  }, []);

  return {
    confirm,
    dialogProps: {
      showDialogFlag: enabled && showDialogFlag,
      handleClose: cancel,
      showDialog: cancel,
      confirmDialog: accept,
    },
  };
};
